/**
 * Quick Edit Panel - Contextual editing from Arena Explorer
 * Elvis Aloha Collection
 * 
 * Supports fast contextual updates without leaving the map:
 * - Edit section, row, seat
 * - Update issue and use status
 * - Add public description
 * - Add private research notes
 * - Change evidence confidence
 * - Add verification status
 * - Attach existing media
 * - Link to full dashboard record
 * 
 * DOES NOT support (use dashboard instead):
 * - Merging records
 * - Bulk imports
 * - Private owner management
 * - Audit review
 * - Destructive actions
 */

import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Status options for dropdowns
const ISSUE_STATUS_OPTIONS = [
  { value: 'status_under_review', label: 'Status Under Review' },
  { value: 'confirmed_issued', label: 'Confirmed Issued' },
  { value: 'possibly_issued', label: 'Possibly Issued' },
  { value: 'possibly_not_issued', label: 'Possibly Not Issued' },
  { value: 'confirmed_not_issued', label: 'Confirmed Not Issued' },
  { value: 'issue_status_unknown', label: 'Unknown' },
];

const USE_STATUS_OPTIONS = [
  { value: 'use_under_review', label: 'Use Under Review' },
  { value: 'confirmed_used', label: 'Confirmed Used' },
  { value: 'possibly_used', label: 'Possibly Used' },
  { value: 'possibly_unused', label: 'Possibly Unused' },
  { value: 'confirmed_unused', label: 'Confirmed Unused' },
  { value: 'use_status_unknown', label: 'Unknown' },
];

const EVIDENCE_CONFIDENCE_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'research_pending', label: 'Research Pending' },
];

const VERIFICATION_OPTIONS = [
  { value: 'visual_review', label: 'Visual Review Passed' },
  { value: 'physical_inspection', label: 'Expert Physical Inspection Passed' },
  { value: 'psa_graded', label: 'PSA Graded' },
  { value: 'other_grading', label: 'Other Grading Completed' },
  { value: 'provenance_documented', label: 'Provenance Documented' },
  { value: 'graceland_displayed', label: 'Displayed at Graceland' },
];

const SelectField = ({ label, value, options, onChange, disabled }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={{
      display: 'block',
      fontSize: '11px',
      fontWeight: 600,
      color: '#888',
      marginBottom: '6px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }}>
      {label}
    </label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        fontSize: '14px',
        background: disabled ? '#f5f5f5' : 'white',
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}
    >
      <option value="">—</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const TextAreaField = ({ label, value, onChange, placeholder, disabled, rows = 3 }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={{
      display: 'block',
      fontSize: '11px',
      fontWeight: 600,
      color: '#888',
      marginBottom: '6px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }}>
      {label}
    </label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      style={{
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        fontSize: '14px',
        resize: 'vertical',
        fontFamily: "'Source Sans 3', sans-serif",
        boxSizing: 'border-box',
        background: disabled ? '#f5f5f5' : 'white'
      }}
    />
  </div>
);

const InputField = ({ label, value, onChange, type = 'text', disabled }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={{
      display: 'block',
      fontSize: '11px',
      fontWeight: 600,
      color: '#888',
      marginBottom: '6px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }}>
      {label}
    </label>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        fontSize: '14px',
        boxSizing: 'border-box',
        background: disabled ? '#f5f5f5' : 'white'
      }}
    />
  </div>
);

const QuickEditPanel = ({ 
  ticket, 
  onClose, 
  onSave,
  onOpenFullRecord
}) => {
  const [formData, setFormData] = useState({
    section: ticket?.section || '',
    row: ticket?.row || '',
    seat: ticket?.seat || '',
    issue_status: ticket?.issue_status || '',
    use_status: ticket?.use_status || '',
    evidence_confidence: ticket?.evidence_confidence || '',
    historical_description: ticket?.historical_description || '',
    research_notes_private: ticket?.research_notes_private || '',
  });
  
  const [selectedVerification, setSelectedVerification] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess(false);
  };
  
  const handleSave = async () => {
    if (!ticket?.id) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/tickets/${ticket.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section: formData.section,
          row: formData.row ? parseInt(formData.row) : null,
          seat: formData.seat ? parseInt(formData.seat) : null,
          issue_status: formData.issue_status || null,
          use_status: formData.use_status || null,
          evidence_confidence: formData.evidence_confidence || null,
          historical_description: formData.historical_description || null,
          research_notes_private: formData.research_notes_private || null,
        })
      });
      
      if (res.ok) {
        setSuccess(true);
        if (onSave) {
          const updated = await res.json();
          onSave(updated);
        }
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to save changes');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleAddVerification = async () => {
    if (!selectedVerification || !ticket?.id) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/tickets/${ticket.id}/verifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          review_type: selectedVerification,
          result: 'passed',
          confidence: 'high',
          public_summary: `${selectedVerification.replace(/_/g, ' ')} completed.`
        })
      });
      
      if (res.ok) {
        setSelectedVerification('');
        setSuccess(true);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to add verification');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setSaving(false);
    }
  };
  
  if (!ticket) return null;
  
  return (
    <div
      data-testid="quick-edit-panel"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '380px',
        height: '100vh',
        background: 'white',
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
        zIndex: 1500,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #eee',
        background: '#fafafa',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{
              fontSize: '10px',
              color: '#C9A962',
              fontWeight: 700,
              letterSpacing: '0.12em',
              marginBottom: '6px'
            }}>
              QUICK EDIT
            </div>
            <h3 style={{
              margin: 0,
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '20px',
              fontWeight: 500
            }}>
              {ticket.section ? `Section ${ticket.section}` : 'Ticket Record'}
            </h3>
            <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
              {ticket.registry_reference}
            </div>
          </div>
          
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#888',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            aria-label="Close panel"
          >
            ×
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '24px'
      }}>
        {/* Messages */}
        {error && (
          <div style={{
            padding: '12px 14px',
            background: 'rgba(212, 68, 68, 0.1)',
            border: '1px solid rgba(212, 68, 68, 0.3)',
            borderRadius: '6px',
            color: '#c44',
            fontSize: '13px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{
            padding: '12px 14px',
            background: 'rgba(72, 187, 120, 0.1)',
            border: '1px solid rgba(72, 187, 120, 0.3)',
            borderRadius: '6px',
            color: '#38a169',
            fontSize: '13px',
            marginBottom: '20px'
          }}>
            Changes saved successfully
          </div>
        )}
        
        {/* Location */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            margin: '0 0 12px',
            fontSize: '12px',
            fontWeight: 700,
            color: '#666',
            letterSpacing: '0.05em'
          }}>
            LOCATION
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <InputField 
              label="Section" 
              value={formData.section}
              onChange={(v) => updateField('section', v)}
            />
            <InputField 
              label="Row" 
              value={formData.row}
              onChange={(v) => updateField('row', v)}
              type="number"
            />
            <InputField 
              label="Seat" 
              value={formData.seat}
              onChange={(v) => updateField('seat', v)}
              type="number"
            />
          </div>
        </div>
        
        {/* Status */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            margin: '0 0 12px',
            fontSize: '12px',
            fontWeight: 700,
            color: '#666',
            letterSpacing: '0.05em'
          }}>
            STATUS
          </h4>
          
          <SelectField
            label="Issue Status"
            value={formData.issue_status}
            options={ISSUE_STATUS_OPTIONS}
            onChange={(v) => updateField('issue_status', v)}
          />
          
          <SelectField
            label="Use Status"
            value={formData.use_status}
            options={USE_STATUS_OPTIONS}
            onChange={(v) => updateField('use_status', v)}
          />
          
          <SelectField
            label="Evidence Confidence"
            value={formData.evidence_confidence}
            options={EVIDENCE_CONFIDENCE_OPTIONS}
            onChange={(v) => updateField('evidence_confidence', v)}
          />
        </div>
        
        {/* Description */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            margin: '0 0 12px',
            fontSize: '12px',
            fontWeight: 700,
            color: '#666',
            letterSpacing: '0.05em'
          }}>
            DESCRIPTION
          </h4>
          
          <TextAreaField
            label="Public Historical Description"
            value={formData.historical_description}
            onChange={(v) => updateField('historical_description', v)}
            placeholder="A brief public-facing description of this ticket..."
            rows={3}
          />
          
          <TextAreaField
            label="Private Research Notes"
            value={formData.research_notes_private}
            onChange={(v) => updateField('research_notes_private', v)}
            placeholder="Internal notes (not visible to public)..."
            rows={3}
          />
        </div>
        
        {/* Add Verification */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            margin: '0 0 12px',
            fontSize: '12px',
            fontWeight: 700,
            color: '#666',
            letterSpacing: '0.05em'
          }}>
            ADD VERIFICATION
          </h4>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <select
              value={selectedVerification}
              onChange={(e) => setSelectedVerification(e.target.value)}
              style={{
                flex: 1,
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Select verification type...</option>
              {VERIFICATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            
            <button
              onClick={handleAddVerification}
              disabled={!selectedVerification || saving}
              style={{
                padding: '10px 16px',
                background: selectedVerification ? '#C9A962' : '#ddd',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: selectedVerification ? 'pointer' : 'not-allowed'
              }}
            >
              Add
            </button>
          </div>
        </div>
        
        {/* Media attachment placeholder */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            margin: '0 0 12px',
            fontSize: '12px',
            fontWeight: 700,
            color: '#666',
            letterSpacing: '0.05em'
          }}>
            MEDIA
          </h4>
          
          <button
            style={{
              width: '100%',
              padding: '16px',
              background: '#f5f5f5',
              border: '1px dashed #ccc',
              borderRadius: '8px',
              color: '#888',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            + Attach Existing Media
          </button>
        </div>
      </div>
      
      {/* Footer Actions */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #eee',
        background: '#fafafa',
        display: 'flex',
        gap: '12px',
        flexShrink: 0
      }}>
        <button
          onClick={onOpenFullRecord}
          style={{
            flex: 1,
            padding: '14px',
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          Full Record →
        </button>
        
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            flex: 1,
            padding: '14px',
            background: '#C9A962',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default QuickEditPanel;
